/**
 * 朋友圈/动态 Store
 */

import { useBroadcastChannel, useLocalStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref, toRaw, watch } from 'vue'

import { useIndexedDBStorage, waitForStorage } from '../utils/indexed-db-storage'

export interface PostComment {
  id: string
  author: string // 角色名或 'user'
  authorId: string
  text: string
  timestamp: number
  replyToId?: string // 回复的评论ID（如果是回复某条评论）
  replyToAuthor?: string // 被回复者的名字
  replyToAuthorId?: string // 被回复者的ID
}

export interface Post {
  id: string
  authorId: string // 'user' 或角色 ID
  authorName: string
  authorAvatar?: string
  timestamp: number
  type: 'text' | 'image'
  content: string // 文字内容或图片配文
  imageUrl?: string // 真实图片 URL（用户上传的图片）
  imageDescription?: string // 图片描述（AI 生成的虚拟图片描述，或用户图片的 OCR/描述）
  likes: string[] // 点赞的角色 ID 列表
  comments: PostComment[]
  mentionIds?: string[] // @的角色 ID
  visibleTo?: string[] // 可见联系人 ID 列表（为空表示公开给所有人）
}

export interface PostInteraction {
  id: string
  type: 'new_post' | 'like' | 'comment' | 'reply'
  actorId: string
  actorName: string
  actorAvatar?: string
  postId: string
  postPreview: string
  commentText?: string
  timestamp: number
}

export interface AggregatedInteraction {
  ids: string[]
  type: 'new_post' | 'like' | 'comment' | 'reply'
  postId: string
  postPreview: string
  timestamp: number
  actorId?: string
  actorName?: string
  actorAvatar?: string
  commentText?: string
  actors?: { id: string, name: string, avatar?: string }[]
}

// 📡 BroadcastChannel 事件类型定义
type BroadcastChannelEvents
  = | { type: 'new_post', data: { post: Post } }
    | { type: 'post_liked', data: { postId: string, userId: string } }
    | { type: 'post_unliked', data: { postId: string, userId: string } }
    | { type: 'post_commented', data: { postId: string, comment: PostComment } }
    | { type: 'post_deleted', data: { postId: string } }
    | { type: 'interaction_added', data: { interaction: PostInteraction } }
    | { type: 'interaction_removed', data: { interactionIds: string[] } }
    | { type: 'interactions_cleared' }

export const usePostsStore = defineStore('posts', () => {
  // ✨ 使用 IndexedDB 持久化（动态列表可能包含大量数据）
  const postsStorage = useIndexedDBStorage<Post[]>('posts/list', [], {
    debounceMs: 300,
  })
  const posts = postsStorage.data

  // 等待存储加载完成
  const postsLoaded = ref(false)
  waitForStorage(postsStorage).then(() => {
    postsLoaded.value = true
    console.debug('✅ 朋友圈存储加载完成（IndexedDB）')
  })

  // 🔔 互动通知存储（IndexedDB）
  const interactionsStorage = useIndexedDBStorage<PostInteraction[]>('posts/interactions', [], {
    debounceMs: 300,
  })
  const interactions = interactionsStorage.data

  const interactionsLoaded = ref(false)
  waitForStorage(interactionsStorage).then(() => {
    interactionsLoaded.value = true
    console.debug('✅ 互动通知存储加载完成（IndexedDB）')
  })

  // 🔴 朋友圈红点提示 - 上次查看时间（数字很小，留在 localStorage）
  const lastViewedTime = useLocalStorage<number>('posts/last-viewed-time', 0)

  // 📡 跨页面通信 - BroadcastChannel
  const { post: broadcastPost, data: receivedPost } = useBroadcastChannel<
    BroadcastChannelEvents,
    BroadcastChannelEvents
  >({ name: 'meowchat-posts' })

  // 最大保留动态数量
  const MAX_POSTS = 100
  const MAX_INTERACTIONS = 200

  /**
   * 添加互动通知（内部方法）
   */
  function addInteraction(interaction: Omit<PostInteraction, 'id'>) {
    const newInteraction: PostInteraction = {
      ...interaction,
      id: `interaction_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
    }

    interactions.value.unshift(newInteraction)

    if (interactions.value.length > MAX_INTERACTIONS) {
      interactions.value = interactions.value.slice(0, MAX_INTERACTIONS)
    }

    broadcastPost({
      type: 'interaction_added',
      data: { interaction: JSON.parse(JSON.stringify(newInteraction)) },
    })
  }

  /**
   * 创建新动态
   */
  function createPost(post: Omit<Post, 'id' | 'timestamp' | 'likes' | 'comments'> & { timestamp?: number }) {
    const ts = post.timestamp || Date.now()
    const newPost: Post = {
      ...post,
      id: `post_${ts}_${Math.random().toString(36).substring(2, 11)}`,
      timestamp: ts,
      likes: [],
      comments: [],
    }

    // 插入到正确的时间位置（保持按时间降序排列）
    const insertIndex = posts.value.findIndex(p => p.timestamp < ts)
    if (insertIndex === -1) {
      posts.value.push(newPost) // 最旧的，放末尾
    }
    else {
      posts.value.splice(insertIndex, 0, newPost)
    }

    // 维护最大数量
    if (posts.value.length > MAX_POSTS) {
      posts.value = posts.value.slice(0, MAX_POSTS)
    }

    console.info('📝 创建新动态:', newPost.authorName, newPost.content.substring(0, 50))

    // 🔔 AI 发帖时生成互动通知（被本机拉黑的联系人不发互动通知）
    if (newPost.authorId !== 'user') {
      // 异步检查拉黑状态后再生成互动通知，避免循环 import
      import('./contacts').then(({ useContactsStore }) => {
        if (!useContactsStore().isFullyBlocked(newPost.authorId)) {
          addInteraction({
            type: 'new_post',
            actorId: newPost.authorId,
            actorName: newPost.authorName,
            actorAvatar: newPost.authorAvatar,
            postId: newPost.id,
            postPreview: newPost.content.substring(0, 30),
            timestamp: newPost.timestamp,
          })
        }
      }).catch(() => {
        // contacts store 不可用：保守放行（旧行为）
        addInteraction({
          type: 'new_post',
          actorId: newPost.authorId,
          actorName: newPost.authorName,
          actorAvatar: newPost.authorAvatar,
          postId: newPost.id,
          postPreview: newPost.content.substring(0, 30),
          timestamp: newPost.timestamp,
        })
      })
    }

    // 📡 广播新动态到其他页面（toRaw 脱离 Vue 响应式代理，防止 DataCloneError）
    broadcastPost({
      type: 'new_post',
      data: { post: JSON.parse(JSON.stringify(toRaw(newPost))) },
    })

    return newPost
  }

  /**
   * 点赞动态
   */
  function likePost(postId: string, userId: string) {
    const post = posts.value.find(p => p.id === postId)
    if (!post) {
      console.warn('动态不存在:', postId)
      return false
    }

    // 检查是否已经点赞
    if (post.likes.includes(userId)) {
      console.warn('已经点赞过了')
      return false
    }

    post.likes.push(userId)
    console.info('❤️ 点赞动态:', post.authorName, '(by', userId, ')')

    // 📡 广播点赞事件到其他页面
    broadcastPost({
      type: 'post_liked',
      data: { postId, userId },
    })

    return true
  }

  /**
   * 取消点赞
   */
  function unlikePost(postId: string, userId: string) {
    const post = posts.value.find(p => p.id === postId)
    if (!post) {
      return false
    }

    const index = post.likes.indexOf(userId)
    if (index > -1) {
      post.likes.splice(index, 1)
      console.info('💔 取消点赞:', post.authorName)

      // 📡 广播取消点赞事件到其他页面
      broadcastPost({
        type: 'post_unliked',
        data: { postId, userId },
      })

      return true
    }

    return false
  }

  /**
   * 评论动态
   * ：当用户评论AI的动态时，自动向AI发送通知
   */
  function commentOnPost(
    postId: string,
    commentText: string,
    authorId: string,
    authorName: string,
    options?: {
      notifyChatStore?: any // 传入 chatStore 以便发送通知消息
      replyTo?: { id: string, author: string, authorId: string } // 回复某条评论
    },
  ) {
    const post = posts.value.find(p => p.id === postId)
    if (!post) {
      console.warn('动态不存在:', postId)
      return null
    }

    const comment: PostComment = {
      id: `comment_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
      author: authorName,
      authorId,
      text: commentText,
      timestamp: Date.now(),
      ...(options?.replyTo && {
        replyToId: options.replyTo.id,
        replyToAuthor: options.replyTo.author,
        replyToAuthorId: options.replyTo.authorId,
      }),
    }

    post.comments.push(comment)
    if (options?.replyTo) {
      console.info('💬 评论动态:', authorName, '回复', options.replyTo.author, ':', commentText.substring(0, 30))
    }
    else {
      console.info('💬 评论动态:', post.authorName, '←', authorName, ':', commentText.substring(0, 30))
    }

    // 📡 广播评论事件到其他页面
    broadcastPost({
      type: 'post_commented',
      data: { postId, comment: JSON.parse(JSON.stringify(comment)) },
    })

    // ⭐ 如果是用户评论AI的动态，发送通知到聊天记录
    if (authorId === 'user' && post.authorId !== 'user' && options?.notifyChatStore) {
      const chatStore = options.notifyChatStore

      // 插入一个系统通知消息
      const notificationMessage = {
        role: 'system' as const,
        content: `[系统提示：用户评论了你的动态。动态ID: ${postId}，评论内容："${commentText}"]`,
        timestamp: Date.now() + 1, // 确保在当前消息之后
        metadata: {
          type: 'user_post_comment', // 新增消息类型
          notificationId: comment.id,
          postId,
          commentText,
          isHidden: true, // 不在UI中显示
        },
      }

      chatStore.addMessage(notificationMessage)
      console.info('📬 已向AI发送动态评论通知')
    }

    return comment
  }

  /**
   * 删除动态
   */
  function deletePost(postId: string) {
    const index = posts.value.findIndex(p => p.id === postId)
    if (index > -1) {
      const deleted = posts.value.splice(index, 1)[0]
      console.info('🗑️ 删除动态:', deleted.authorName)

      // 🔔 清除该动态的所有互动通知
      interactions.value = interactions.value.filter(i => i.postId !== postId)

      // 📡 广播删除事件到其他页面
      broadcastPost({
        type: 'post_deleted',
        data: { postId },
      })

      return true
    }
    return false
  }

  /**
   * 清空所有动态
   */
  function clearAllPosts() {
    posts.value = []
    console.info('🧹 清空所有动态')
  }

  /**
   * 获取某个用户的动态
   */
  function getPostsByAuthor(authorId: string) {
    return posts.value.filter(p => p.authorId === authorId)
  }

  /**
   * 获取提到某个用户的动态
   */
  function getPostsMentioning(userId: string) {
    return posts.value.filter(p => p.mentionIds?.includes(userId))
  }

  /**
   * 获取对某个角色可见的动态
   * @param viewerId 查看者的 ID（'user' 或联系人 ID）
   */
  function getVisiblePosts(viewerId: string) {
    return posts.value.filter((post) => {
      // 如果没有设置 visibleTo，则对所有人可见
      if (!post.visibleTo || post.visibleTo.length === 0) {
        return true
      }
      // 如果设置了 visibleTo，检查查看者是否在列表中
      return post.visibleTo.includes(viewerId)
    })
  }

  /**
   * AI 点赞动态
   */
  function aiLikePost(postId: string, aiId: string, aiName: string) {
    const post = posts.value.find(p => p.id === postId)
    if (!post) {
      console.warn('动态不存在:', postId)
      return false
    }

    // 检查是否已经点赞
    if (post.likes.includes(aiId)) {
      console.warn('AI已经点赞过了')
      return false
    }

    post.likes.push(aiId)
    console.info('❤️ AI点赞动态:', aiName, '→', post.authorName)

    // 🔔 生成点赞互动通知
    addInteraction({
      type: 'like',
      actorId: aiId,
      actorName: aiName,
      postId,
      postPreview: post.content.substring(0, 30),
      timestamp: Date.now(),
    })

    // 📡 广播点赞事件到其他页面
    broadcastPost({
      type: 'post_liked',
      data: { postId, userId: aiId },
    })

    return true
  }

  /**
   * AI 评论动态
   */
  function aiCommentOnPost(
    postId: string,
    commentText: string,
    aiId: string,
    aiName: string,
    replyTo?: { id: string, author: string, authorId: string },
  ) {
    const post = posts.value.find(p => p.id === postId)
    if (!post) {
      console.warn('动态不存在:', postId)
      return null
    }

    const comment: PostComment = {
      id: `comment_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
      author: aiName,
      authorId: aiId,
      text: commentText,
      timestamp: Date.now(),
      ...(replyTo && {
        replyToId: replyTo.id,
        replyToAuthor: replyTo.author,
        replyToAuthorId: replyTo.authorId,
      }),
    }

    post.comments.push(comment)
    if (replyTo) {
      console.info('💬 AI评论动态:', aiName, '回复', replyTo.author, ':', commentText.substring(0, 30))
    }
    else {
      console.info('💬 AI评论动态:', aiName, '→', post.authorName, ':', commentText.substring(0, 30))
    }

    // 🔔 生成评论/回复互动通知
    addInteraction({
      type: replyTo ? 'reply' : 'comment',
      actorId: aiId,
      actorName: aiName,
      postId,
      postPreview: post.content.substring(0, 30),
      commentText: commentText.substring(0, 50),
      timestamp: Date.now(),
    })

    // 📡 广播评论事件到其他页面
    broadcastPost({
      type: 'post_commented',
      data: { postId, comment: JSON.parse(JSON.stringify(comment)) },
    })

    return comment
  }

  // 计算属性：按时间排序的动态列表
  const sortedPosts = computed(() => {
    return [...posts.value].sort((a, b) => b.timestamp - a.timestamp)
  })

  // 计算属性：动态总数
  const totalPosts = computed(() => posts.value.length)

  // 🔴 计算属性：未读动态数量
  const unreadPostsCount = computed(() => {
    if (!lastViewedTime.value) {
      return posts.value.length
    }
    return posts.value.filter(post => post.timestamp > lastViewedTime.value).length
  })

  // 🔴 标记朋友圈为已读
  function markPostsAsRead() {
    lastViewedTime.value = Date.now()
    console.info('✅ 朋友圈已标记为已读')
  }

  // 🔔 互动通知管理方法

  function removeInteractions(ids: string[]) {
    const idSet = new Set(ids)
    interactions.value = interactions.value.filter(i => !idSet.has(i.id))
    broadcastPost({
      type: 'interaction_removed',
      data: { interactionIds: ids },
    })
  }

  function removeInteractionsByPostId(postId: string, type?: PostInteraction['type']) {
    const toRemove = interactions.value.filter(i =>
      i.postId === postId && (!type || i.type === type),
    )
    if (toRemove.length > 0) {
      removeInteractions(toRemove.map(i => i.id))
    }
  }

  function clearAllInteractions() {
    interactions.value = []
    broadcastPost({ type: 'interactions_cleared' } as BroadcastChannelEvents)
    console.info('🧹 清空所有互动通知')
  }

  // 🔔 互动通知计算属性

  const unreadInteractionsCount = computed(() => interactions.value.length)

  const totalUnreadCount = computed(() => unreadPostsCount.value + unreadInteractionsCount.value)

  const aggregatedInteractions = computed<AggregatedInteraction[]>(() => {
    const result: AggregatedInteraction[] = []
    const likeGroups = new Map<string, PostInteraction[]>()

    for (const interaction of interactions.value) {
      if (interaction.type === 'like') {
        const group = likeGroups.get(interaction.postId) || []
        group.push(interaction)
        likeGroups.set(interaction.postId, group)
      }
      else {
        result.push({
          ids: [interaction.id],
          type: interaction.type,
          postId: interaction.postId,
          postPreview: interaction.postPreview,
          timestamp: interaction.timestamp,
          actorId: interaction.actorId,
          actorName: interaction.actorName,
          actorAvatar: interaction.actorAvatar,
          commentText: interaction.commentText,
        })
      }
    }

    for (const [postId, likes] of likeGroups) {
      const sorted = [...likes].sort((a, b) => b.timestamp - a.timestamp)
      result.push({
        ids: sorted.map(l => l.id),
        type: 'like',
        postId,
        postPreview: sorted[0].postPreview,
        timestamp: sorted[0].timestamp,
        actorId: sorted[0].actorId,
        actorName: sorted[0].actorName,
        actorAvatar: sorted[0].actorAvatar,
        actors: sorted.map(l => ({ id: l.actorId, name: l.actorName, avatar: l.actorAvatar })),
      })
    }

    return result.sort((a, b) => b.timestamp - a.timestamp)
  })

  // 📡 监听跨页面动态广播
  watch(receivedPost, (event) => {
    if (!event)
      return

    console.info('📨 收到朋友圈广播:', event.type)

    switch (event.type) {
      case 'new_post':
        console.info('📝 其他页面发布了新动态')
        // LocalStorage 会自动同步，不需要手动处理
        break

      case 'post_liked':
        console.info('❤️ 其他页面点赞了动态')
        // LocalStorage 会自动同步
        break

      case 'post_unliked':
        console.info('💔 其他页面取消点赞了动态')
        // LocalStorage 会自动同步
        break

      case 'post_commented':
        console.info('💬 其他页面评论了动态')
        // LocalStorage 会自动同步
        break

      case 'post_deleted':
        console.info('🗑️  其他页面删除了动态')
        // LocalStorage 会自动同步
        break

      case 'interaction_added':
        if (event.data && 'interaction' in event.data) {
          const incoming = event.data.interaction
          if (!interactions.value.some(i => i.id === incoming.id)) {
            interactions.value.unshift(incoming)
            if (interactions.value.length > MAX_INTERACTIONS) {
              interactions.value = interactions.value.slice(0, MAX_INTERACTIONS)
            }
          }
        }
        break

      case 'interaction_removed':
        if (event.data && 'interactionIds' in event.data) {
          const idSet = new Set(event.data.interactionIds)
          interactions.value = interactions.value.filter(i => !idSet.has(i.id))
        }
        break

      case 'interactions_cleared':
        interactions.value = []
        break
    }
  })

  /**
   * 根据ID获取动态
   */
  function getPostById(postId: string): Post | undefined {
    return posts.value.find(p => p.id === postId)
  }

  /**
   * 从文本中提取@提及的联系人ID
   * 格式: @联系人名
   */
  function extractMentionIds(text: string, contactsStore: any): string[] {
    const mentionPattern = /@([\u4E00-\u9FA5\w\s]+?)(?=\s|$|[@，。！？,.!?])/g
    const matches = [...text.matchAll(mentionPattern)]
    const mentionIds: string[] = []

    for (const match of matches) {
      const mentionedName = match[1].trim()
      // 查找联系人（拉黑过滤：已被双向拉黑的联系人不应被 @）
      const contact = contactsStore.contactsList.find((c: any) =>
        c.name === mentionedName && !c.isGroup && !contactsStore.isFullyBlocked(c.id),
      )
      if (contact) {
        mentionIds.push(contact.id)
      }
    }

    return mentionIds
  }

  /**
   * 清理某个联系人的所有相关引用（朋友圈孤儿数据）
   * - 删除该联系人作为 author 的 posts
   * - 删除该联系人的 comments
   * - 从 likes / visibleTo 中移除
   */
  function purgeByContactId(contactId: string) {
    if (!contactId)
      return
    const before = posts.value.length
    posts.value = posts.value
      .filter(p => p.authorId !== contactId)
      .map((p) => {
        const cleaned = {
          ...p,
          comments: (p.comments || []).filter(c => c.authorId !== contactId),
          likes: (p.likes || []).filter(id => id !== contactId),
          visibleTo: p.visibleTo ? p.visibleTo.filter(id => id !== contactId) : p.visibleTo,
        }
        return cleaned
      })
    if (before !== posts.value.length || true)
      console.info(`🧹 [posts] 清理联系人 ${contactId} 的引用，剩余 ${posts.value.length} 条 post`)
  }

  return {
    // 状态
    posts,
    sortedPosts,
    totalPosts,
    unreadPostsCount,
    lastViewedTime,

    // 🔔 互动通知
    interactions,
    unreadInteractionsCount,
    totalUnreadCount,
    aggregatedInteractions,

    // 方法
    createPost,
    likePost,
    unlikePost,
    commentOnPost,
    deletePost,
    purgeByContactId,
    clearAllPosts,
    getPostsByAuthor,
    getPostsMentioning,
    getVisiblePosts,
    aiLikePost,
    aiCommentOnPost,
    getPostById,
    extractMentionIds,
    markPostsAsRead,

    // 🔔 互动通知方法
    removeInteractions,
    removeInteractionsByPostId,
    clearAllInteractions,
  }
})
